import { ApiError } from './ApiError'

/** The API's per-user rate limiter rejects with this. */
const TOO_MANY_REQUESTS = 429

/**
 * Run async task thunks with at most `limit` running at once, preserving
 * result order. Unlike `Promise.all` over already-started promises, this bounds
 * concurrency so a large batch of writes doesn't flood the API gateway all at
 * once (CLUB-195: a burst of simultaneous saves returned 502s / partial saves).
 *
 * Every task runs even when an earlier one fails; the first failure is rethrown
 * once the pool has drained. Bailing out on the first rejection was worse than
 * it looked: `Promise.all` rejected while the other workers kept pulling tasks
 * unawaited, so the caller was handed one error for a batch that was still
 * running and whose eventual coverage nothing observed.
 */
export async function runPooled<T>(
  tasks: ReadonlyArray<() => Promise<T>>,
  limit: number,
): Promise<T[]> {
  const results = new Array<T>(tasks.length)
  // Keyed by task index, not by completion order: workers finish out of order,
  // so "the first failure" has to mean the earliest task that failed or the
  // reported error would vary run to run for the same input.
  const failures = new Map<number, unknown>()
  let cursor = 0
  const worker = async (): Promise<void> => {
    // No await between reading and advancing the cursor, so single-threaded JS
    // guarantees each task is claimed by exactly one worker.
    while (cursor < tasks.length) {
      const index = cursor++
      try {
        results[index] = await tasks[index]()
      } catch (err) {
        failures.set(index, err)
      }
    }
  }
  // A non-finite limit would make `Array.from({ length })` empty, which resolves
  // immediately having written nothing and reports success — the worst possible
  // outcome for a save.
  const bounded = Number.isFinite(limit) ? Math.min(limit, tasks.length) : 1
  const size = Math.max(1, bounded)
  await Promise.all(Array.from({ length: size }, () => worker()))
  if (failures.size > 0) {
    throw failures.get(Math.min(...failures.keys()))
  }
  return results
}

export interface RetryOptions {
  /** Number of retries after the first attempt. Default 3. */
  retries?: number
  /** Base backoff in ms (doubles each attempt). Default 300. */
  baseDelayMs?: number
  /** Injectable delay, for tests. */
  sleep?: (ms: number) => Promise<void>
}

/**
 * Transient failures worth retrying: 5xx (e.g. a 502 from an overwhelmed
 * gateway), 429, and network errors (fetch rejects with a TypeError). Every
 * other 4xx is the caller's fault and is never retried.
 *
 * 429 belongs here because the limiter is per-user across the whole API and a
 * map save spends the entire budget by itself: it writes one request per map
 * object, four at a time, which outruns 100/minute within seconds. Treating
 * that as the caller's fault dropped the rest of the save on the floor.
 */
export function isTransientError(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === TOO_MANY_REQUESTS
  return err instanceof TypeError
}

/**
 * How long to wait before the next attempt. A server that said when to come
 * back is obeyed exactly; otherwise the wait doubles each attempt. Guessing
 * against a fixed window is what makes exponential backoff the wrong tool
 * here — the limiter knows when the window reopens and says so.
 */
export function retryDelayMs(err: unknown, attempt: number, baseDelayMs: number): number {
  if (err instanceof ApiError && err.retryAfterSeconds !== undefined) {
    // Floored at the base delay. The HTTP-date form of the header is a SERVER
    // instant measured against the CLIENT's clock, so a browser running a
    // minute fast turns every date-form header negative, which clamps to zero
    // and spends the whole retry budget in about no time. Our API sends
    // delta-seconds today, but this module handles both forms and an
    // intermediary can answer 429 or 503 with a date.
    return Math.max(err.retryAfterSeconds * 1000, baseDelayMs)
  }
  return baseDelayMs * 2 ** attempt
}

/**
 * Await `fn`, retrying transient failures.
 *
 * The budget is a fixed number of attempts, so a save large enough to outlast
 * that many rate-limit windows still loses its tail. The durable fix for that
 * is a bulk write endpoint that makes a large save one request instead of
 * hundreds, not a longer retry chain.
 */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const retries = opts.retries ?? 3
  const baseDelayMs = opts.baseDelayMs ?? 300
  const sleep = opts.sleep ?? ((ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn()
    } catch (err) {
      if (attempt >= retries || !isTransientError(err)) throw err
      await sleep(retryDelayMs(err, attempt, baseDelayMs))
    }
  }
}
